import { formatDistanceToNow } from 'date-fns'
import { MessageSquare, X } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  useDismissFeedbackPrompt,
  usePendingFeedbackPrompts,
  type FeedbackPrompt,
} from '@/hooks/use-feedback'
import { feedbackKindBadgeVariant, formatFeedbackKind } from '@/lib/feedback-display'
import type { FeedbackKind } from '@/hooks/use-feedback'

export type FeedbackPromptsSectionProps = {
  /** Hands the prompt question to the chat composer; the agent records the answer. */
  onStartConversation?: (message: string) => void
}

function buildAnswerKickoffMessage(prompt: FeedbackPrompt): string {
  return `The team asked: "${prompt.question}" — here's my answer: `
}

/**
 * "From the team" band on the feedback board: the pending prompts behind the
 * header megaphone badge. Answering (via chat) or dismissing clears the count.
 */
export function FeedbackPromptsSection({ onStartConversation }: FeedbackPromptsSectionProps) {
  const { prompts } = usePendingFeedbackPrompts()
  const { dismissPrompt, isDismissing } = useDismissFeedbackPrompt()

  if (prompts.length === 0) return null

  const handleDismiss = (prompt: FeedbackPrompt) => {
    dismissPrompt(prompt.id, {
      onError: (err: Error) => {
        toast.error(err.message || 'Failed to dismiss prompt')
      },
    })
  }

  return (
    <section aria-label="Questions from the team" className="space-y-2">
      <div className="space-y-0.5">
        <h2 className="text-sm font-semibold text-foreground">From the team</h2>
        <p className="text-xs text-muted-foreground">
          The product team would love your take — answering or dismissing clears the
          notification on the megaphone icon.
        </p>
      </div>
      <div className="space-y-2">
        {prompts.map((prompt) => (
          <Card key={prompt.id}>
            <CardContent className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {prompt.kind_hint ? (
                    <Badge variant={feedbackKindBadgeVariant(prompt.kind_hint as FeedbackKind)}>
                      {formatFeedbackKind(prompt.kind_hint)}
                    </Badge>
                  ) : null}
                  {prompt.created_at ? (
                    <span className="text-xs text-muted-foreground">
                      {formatDistanceToNow(new Date(prompt.created_at), { addSuffix: true })}
                    </span>
                  ) : null}
                </div>
                <p className="min-w-0 break-words text-sm font-medium text-foreground">
                  {prompt.question}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1.5">
                {onStartConversation ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => onStartConversation(buildAnswerKickoffMessage(prompt))}
                  >
                    <MessageSquare className="mr-1.5 h-3.5 w-3.5" />
                    Answer in chat
                  </Button>
                ) : null}
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  disabled={isDismissing}
                  onClick={() => handleDismiss(prompt)}
                  aria-label={`Dismiss prompt: ${prompt.question}`}
                >
                  <X className="mr-1 h-3.5 w-3.5" />
                  Dismiss
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  )
}
